import Swiper from 'swiper';
import {
	A11y, Navigation, Pagination,
}             from 'swiper/modules';

/**
 * Creates a publications slider using Swiper library
 *
 * @param {HTMLElement} el - The element that contains the slider
 * @returns {Swiper|false} - The created Swiper instance
 *
 * The slider is disabled if the window width is less than 521px.
 * The slider is enabled if the window width is greater than or equal
 * to 521px.
 *
 * The slider has the following settings:
 *
 * - `autoHeight` is set to `false` if the window width is greater than or
 *   equal to 521px, and `true` otherwise.
 * - `modules` is set to `[A11y, Navigation, Pagination]`.
 * - `loop` is set to `false`.
 * - `slidesPerView` is set to `'auto'`.
 * - `spaceBetween` is set to `0`.
 * - `a11y` is set to an object with the following properties:
 *   - `prevSlideMessage` is set to `'Предыдущий слайд'`.
 *   - `nextSlideMessage` is set to `'Следующий слайд'`.
 *   - `paginationBulletMessage` is set to `'Перейти к {{index}}'`.
 *   - `firstSlideMessage` is set to `'Первый слайд'`.
 * - `navigation` is set to an object with the following properties:
 *   - `nextEl` is set to the element with class `'swiper-button-next'`.
 *   - `prevEl` is set to the element with class `'swiper-button-prev'`.
 * - `pagination` is set to an object with the following properties:
 *   - `el` is set to the element with class `'swiper-pagination'`.
 *   - `type` is set to `'bullets'`.
 *   - `clickable` is set to `true`.
 *   - `dynamicBullets` is set to `false`.
 */
export default function publications(el) {
	if (!el) return false;
	
	const elements = {
		slider     : el.querySelector('.swiper'),
		next       : el.querySelector('.swiper-button-next'),
		prev       : el.querySelector('.swiper-button-prev'),
		pagination : el.querySelector('.swiper-pagination'),
	};
	
	const breakpoint = window.matchMedia('(min-width: 521px)');
	
	const baseSettings = {
		modules       : [A11y, Navigation, Pagination],
		loop          : false,
		slidesPerView : 'auto',
		spaceBetween  : 0,
		a11y          : {
			prevSlideMessage        : 'Предыдущий слайд',
			nextSlideMessage        : 'Следующий слайд',
			paginationBulletMessage : 'Перейти к {{index}}',
			firstSlideMessage       : 'Первый слайд',
		},
		navigation : {
			nextEl : elements.next,
			prevEl : elements.prev,
		},
		pagination : {
			el             : elements.pagination,
			type           : 'bullets',
			clickable      : true,
			dynamicBullets : false,
		},
	};
	
	let swiper;
	
	/**
	 * Creates a new Swiper instance and assigns it to the `swiper` variable.
	 *
	 * If a Swiper instance already exists, it is destroyed first.
	 *
	 * The `autoHeight` option is set to `isMobile`.
	 *
	 * @param {boolean} isMobile - Whether the slider is for mobile devices
	 *
	 * @return {void}
	 */
	const createSlider = (isMobile) => {
		if (swiper) swiper.destroy(true, true);
		swiper = new Swiper(elements.slider, {
			...baseSettings,
			autoHeight : isMobile,
		});
	};
	
	/**
	 * Checks if the window matches the breakpoint and creates a new slider
	 * instance with the corresponding settings.
	 *
	 * If the window matches the breakpoint, the slider is created with the
	 * `autoHeight` option set to `false`.
	 *
	 * If the window does not match the breakpoint, the slider is created with
	 * the `autoHeight` option set to `true`.
	 *
	 * @return {void}
	 */
	const breakpointChecker = () => {
		createSlider(!breakpoint.matches);
	};
	try {
		breakpoint.addEventListener('change', breakpointChecker);
	} catch (e) {
		// Safari <14
		breakpoint.addListener(breakpointChecker);
	}
	breakpointChecker();
	
	return swiper;
}
